import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';

// How many minutes before scheduled end to fire the evening reminder
const REMIND_BEFORE_END_MIN = 5;
// Don't show morning reminder before this hour (9 = 9 AM)
const MORNING_REMIND_AFTER_HOUR = 9;

function parseHHMM(str) {
  const [h, m] = (str || '19:00').split(':').map(Number);
  return { h, m };
}

function minutesUntil(targetH, targetM) {
  const now = new Date();
  const target = new Date();
  target.setHours(targetH, targetM, 0, 0);
  return (target - now) / 60000;
}


function requestDesktopPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function sendDesktopNotification(title, body, onClick) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const n = new Notification(title, {
    body,
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: 'worktracker-attendance',
    requireInteraction: true,
  });
  if (onClick) n.onclick = () => { window.focus(); onClick(); n.close(); };
}

// ── Modal shell ──────────────────────────────────────────────────────────────
function ReminderModal({ icon, title, message, children, onClose }) {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(0,0,0,0.45)', backdropFilter:'blur(2px)',
    }}>
      <div style={{
        background:'var(--surface)', borderRadius:16, padding:'32px 28px', maxWidth:380, width:'90%',
        boxShadow:'0 20px 60px rgba(0,0,0,0.3)', textAlign:'center', animation:'fadeInUp 0.2s ease',
      }}>
        <div style={{ fontSize:48, marginBottom:12 }}>{icon}</div>
        <div style={{ fontSize:18, fontWeight:800, color:'var(--text1)', marginBottom:8 }}>{title}</div>
        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:24, lineHeight:1.5 }}>{message}</div>
        {children}
        {onClose && (
          <button onClick={onClose}
            style={{ marginTop:12, fontSize:11, color:'var(--text3)', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main component — drop inside Layout, renders nothing when idle ────────────
export default function AttendanceReminder({ user }) {
  const navigate    = useNavigate();
  const qc          = useQueryClient();
  const [modal, setModal]       = useState(null); // 'morning' | 'evening' | null
  const [clocking, setClocking] = useState(false);
  const eveningTimer = useRef(null);
  const snoozeTimer  = useRef(null);

  const { data: todayData, refetch } = useQuery({
    queryKey: ['att-today', user?.id],
    queryFn:  () => api.get('/attendance-sessions/today').then(r => r.data),
    enabled:  !!user && user.role !== 'accountant',
    staleTime: 60000,
  });

  const session  = todayData?.session;
  const schedule = todayData?.schedule;
  const dayOff   = todayData?.dayOff;

  // ── Request desktop permission on first load ────────────────────────────
  useEffect(() => { requestDesktopPermission(); }, []);

  // ── Morning reminder ────────────────────────────────────────────────────
  useEffect(() => {
    if (!schedule || dayOff) return;
    if (session?.clock_in) return; // already clocked in
    const now = new Date();
    const hour = now.getHours();
    if (hour < MORNING_REMIND_AFTER_HOUR) return; // too early
    if (hour >= 20) return; // too late, don't nag
    // Show morning reminder once
    const dismissed = localStorage.getItem('att-morning-dismissed-' + new Date().toISOString().slice(0,10));
    if (!dismissed) setModal('morning');
  }, [session, schedule, dayOff]);

  // ── Evening reminder setup ──────────────────────────────────────────────
  const scheduleEveningReminder = useCallback(() => {
    if (!schedule || dayOff) return;
    if (eveningTimer.current) clearTimeout(eveningTimer.current);

    // The team works to 7 PM every day including Saturdays, so the clock-out reminder always follows the
    // weekday end — the shorter Saturday schedule hours must not trigger it at 2 PM.
    const endStr  = schedule.weekday_end;
    const { h, m } = parseHHMM(endStr);
    // Fire REMIND_BEFORE_END_MIN minutes before end
    const remindM = m - REMIND_BEFORE_END_MIN;
    const remindH = remindM < 0 ? h - 1 : h;
    const remindMin = remindM < 0 ? 60 + remindM : remindM;

    const msUntil = minutesUntil(remindH, remindMin) * 60000;
    if (msUntil < 0) return; // already past

    eveningTimer.current = setTimeout(() => {
      // Only remind if clocked in but not clocked out
      refetch().then(({ data }) => {
        if (data?.session?.clock_in && !data?.session?.clock_out) {
          setModal('evening');
          sendDesktopNotification(
            '⏰ Time to clock out — 8D Business OS™',
            `Your workday ends in ${REMIND_BEFORE_END_MIN} minutes. Don't forget to clock out!`,
            () => setModal('evening')
          );
        }
      });
    }, msUntil);
  }, [schedule, dayOff, refetch]);

  useEffect(() => {
    scheduleEveningReminder();
    return () => { clearTimeout(eveningTimer.current); clearTimeout(snoozeTimer.current); };
  }, [scheduleEveningReminder]);

  // ── Actions ─────────────────────────────────────────────────────────────
  async function handleClockIn() {
    setClocking(true);
    try {
      await api.post('/attendance-sessions/clock-in');
      toast.success('Clocked in!');
      qc.invalidateQueries(['att-today']);
      setModal(null);
      localStorage.setItem('att-morning-dismissed-' + new Date().toISOString().slice(0,10), '1');
    } catch(err) {
      const msg = err.response?.data?.error;
      if (msg === 'Already clocked in today') {
        toast.success('You are already clocked in');
        setModal(null);
      } else {
        // Navigate to clock page so they can retry
        navigate('/attendance-clock');
        setModal(null);
      }
    } finally { setClocking(false); }
  }

  async function handleClockOut() {
    setClocking(true);
    try {
      await api.post('/attendance-sessions/clock-out');
      toast.success('Clocked out — have a great evening!');
      qc.invalidateQueries(['att-today']);
      setModal(null);
    } catch(err) {
      navigate('/attendance-clock');
      setModal(null);
    } finally { setClocking(false); }
  }

  function handleSnooze() {
    setModal(null);
    snoozeTimer.current = setTimeout(() => {
      refetch().then(({ data }) => {
        if (data?.session?.clock_in && !data?.session?.clock_out) {
          setModal('evening');
          sendDesktopNotification(
            '⏰ Clock-out reminder — 8D Business OS™',
            "You snoozed 5 minutes ago. Don't forget to clock out before you leave!",
            () => setModal('evening')
          );
        }
      });
    }, 5 * 60000); // 5 minutes
    toast('Reminder snoozed for 5 minutes', { icon: '⏱' });
  }

  function dismissMorning() {
    localStorage.setItem('att-morning-dismissed-' + new Date().toISOString().slice(0,10), '1');
    setModal(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────
  if (!modal) return null;

  if (modal === 'morning') {
    return (
      <ReminderModal
        icon="☀️"
        title="Good morning!"
        message="You haven't clocked in yet. Click below to start your workday, or go to the Clock In page."
        onClose={dismissMorning}
      >
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <button className="btn-primary" style={{ width:'100%', padding:'12px', fontSize:14, fontWeight:700 }}
            onClick={handleClockIn} disabled={clocking}>
            {clocking ? 'Clocking in…' : '✅ Clock In Now'}
          </button>
          <button className="btn-ghost" style={{ width:'100%', padding:'10px', fontSize:13 }}
            onClick={() => { navigate('/attendance-clock'); dismissMorning(); }}>
            Go to Clock In page →
          </button>
        </div>
      </ReminderModal>
    );
  }

  if (modal === 'evening') {
    return (
      <ReminderModal
        icon="🌆"
        title="Time to wrap up!"
        message={`Your workday ends in ${REMIND_BEFORE_END_MIN} minutes. Clock out when you're done so your hours are recorded correctly.`}
        onClose={() => setModal(null)}
      >
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <button className="btn-primary" style={{ width:'100%', padding:'12px', fontSize:14, fontWeight:700 }}
            onClick={handleClockOut} disabled={clocking}>
            {clocking ? 'Clocking out…' : '🏁 Clock Out Now'}
          </button>
          <button className="btn-ghost" style={{ width:'100%', padding:'10px', fontSize:13 }}
            onClick={handleSnooze}>
            ⏱ Remind me in 5 minutes
          </button>
          <button className="btn-ghost" style={{ width:'100%', padding:'10px', fontSize:13 }}
            onClick={() => { navigate('/attendance-clock'); setModal(null); }}>
            Go to Clock Out page →
          </button>
        </div>
      </ReminderModal>
    );
  }

  return null;
}
